/**
 * PerChildMaterialsReview · iter303ak-fix27 — Accepted · T50 · Action 10
 * ─────────────────────────────────────────────────────────────────────
 * Per-child accordion. For each accepted child fetches
 *   /api/appraisals/{child_id}/materials-overview
 * → shows # suppliers × # lines per supplier + total order value.
 *   • "↗ Open Materials tab" → /appraisals/{child_id}?embed=materials
 *   • "⬇ Download orders ZIP" → /appraisals/{child_id}/materials-order-pdfs.zip
 *   • "Reviewed & ready" checkbox per row
 * Continue disabled until every row is ticked.
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { CheckCircle2, ExternalLink, Package2, FileText, AlertTriangle } from 'lucide-react';

const api = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api` });
api.interceptors.request.use((cfg) => {
  const t = localStorage.getItem('access_token');
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const nz = (n) => `$${Number(n || 0).toLocaleString('en-NZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function PerChildMaterialsReview({ action, capturedData, onNext, onBack, onCancel, canBack, value, onChange }) {
  const params = action.params || {};

  const acceptedChildren = useMemo(() => {
    const decisions = capturedData?.decisions
      || capturedData?.accepted_variants_pick?.decisions
      || capturedData?.await_quote_response?.decisions
      || capturedData?.appraisal?.child_decisions
      || capturedData?.appraisal_record?.child_decisions
      || {};
    return Object.entries(decisions)
      .filter(([, d]) => d?.decision === 'accepted')
      .map(([child_id, d]) => ({ child_id, ...d }));
  }, [capturedData]);

  const [overviews, setOverviews] = useState({});
  const [reviewedIds, setReviewedIds] = useState(() => new Set(value?.reviewed_ids || []));
  const [loading, setLoading] = useState(true);
  const [openWindow, setOpenWindow] = useState({});

  const loadOverviews = useCallback(async () => {
    if (!acceptedChildren.length) { setLoading(false); return; }
    setLoading(true);
    try {
      const results = {};
      for (const c of acceptedChildren) {
        try {
          const { data } = await api.get(`/appraisals/${c.child_id}/materials-overview`);
          results[c.child_id] = data;
        } catch (e) {
          results[c.child_id] = { error: e?.response?.data?.detail || 'Failed to load materials-overview' };
        }
      }
      setOverviews(results);
    } finally { setLoading(false); }
  }, [acceptedChildren]);

  useEffect(() => { loadOverviews(); }, [loadOverviews]);

  // Poll for popup close → refresh that child's overview.
  useEffect(() => {
    const timers = [];
    for (const [cid, win] of Object.entries(openWindow)) {
      if (!win) continue;
      const timer = setInterval(async () => {
        if (win.closed) {
          clearInterval(timer);
          setOpenWindow(prev => ({ ...prev, [cid]: null }));
          try {
            const { data } = await api.get(`/appraisals/${cid}/materials-overview`);
            setOverviews(prev => ({ ...prev, [cid]: data }));
          } catch { /* silent */ }
        }
      }, 500);
      timers.push(timer);
    }
    return () => { timers.forEach(clearInterval); };
  }, [openWindow]);

  const openChildMaterialsTab = (child_id) => {
    const url = `/appraisals/${child_id}?embed=materials`;
    const win = window.open(url, `child_materials_${child_id}`, 'width=1400,height=900');
    if (!win) { alert('Popup blocked — allow popups for this site and try again.'); return; }
    setOpenWindow(prev => ({ ...prev, [child_id]: win }));
  };

  const toggleReviewed = (child_id) => {
    setReviewedIds(prev => {
      const next = new Set(prev);
      if (next.has(child_id)) next.delete(child_id); else next.add(child_id);
      return next;
    });
  };

  useEffect(() => {
    if (onChange) {
      onChange({
        reviewed_ids: Array.from(reviewedIds),
        overviews_snapshot: overviews,
      });
    }
  }, [reviewedIds, overviews, onChange]);

  const allReviewed = acceptedChildren.length > 0 && acceptedChildren.every(c => reviewedIds.has(c.child_id));

  const openFileHref = (child_id) => `${BACKEND_URL}/api/appraisals/${child_id}/materials-order-pdf-combined?inline=1`;

  return (
    <div className="space-y-4" data-testid="wf-per-child-materials-review">
      <div className="rounded-xl border border-teal-200 bg-teal-50/60 p-4">
        <div className="flex items-start gap-3">
          <Package2 className="w-5 h-5 mt-0.5 shrink-0 text-teal-700" />
          <div className="flex-1">
            <div className="text-sm font-semibold text-teal-900">Review Materials Overview — per accepted quote</div>
            <div className="text-sm mt-1 text-teal-800">
              {params.prompt || 'The component data was auto-pushed on T10 (Confirm Acceptance). Open the Materials tab if anything needs correction, then tick each quote as ready.'}
            </div>
          </div>
        </div>
      </div>

      {loading && <div className="p-6 text-sm text-slate-400 italic">Loading materials summaries…</div>}

      {!loading && acceptedChildren.length === 0 && (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded text-sm text-amber-800">
          No accepted quotes found. Go back to Follow-up and confirm at least one.
        </div>
      )}

      {!loading && acceptedChildren.length > 0 && (
        <div className="space-y-2">
          {acceptedChildren.map((c) => {
            const o = overviews[c.child_id] || {};
            const reviewed = reviewedIds.has(c.child_id);
            const label = [c.quote_type_ref, c.variant_label].filter(Boolean).join(' · ');
            const suppliers = o.suppliers || [];
            const totalLines = suppliers.reduce((sum, s) => sum + (s.materials?.length || 0), 0);
            const grandTotal = suppliers.reduce((sum, s) => sum + Number(s.total || 0), 0);
            const hasErr = !!o.error;
            return (
              <div key={c.child_id} className={`p-3 border rounded-lg ${reviewed ? 'bg-teal-50/40 border-teal-300' : 'bg-white border-slate-200'}`} data-testid={`wf-mat-review-row-${c.child_id}`}>
                <div className="flex items-start justify-between gap-3 mb-2 flex-wrap">
                  <div className="flex items-center gap-2 flex-wrap min-w-0">
                    <span className="text-sm font-medium text-slate-800 truncate">{label || 'Quote'}</span>
                    {c.appraisal_no && (
                      <span className="text-[10px] font-bold uppercase tracking-widest text-indigo-800 bg-indigo-100 border border-indigo-300 rounded px-1.5 py-0.5">
                        A{c.appraisal_no}
                      </span>
                    )}
                    {!hasErr && (
                      <>
                        <span className="text-[10px] font-semibold text-slate-500 bg-slate-100 border border-slate-200 rounded px-1.5 py-0.5">
                          {suppliers.length} supplier{suppliers.length === 1 ? '' : 's'}
                        </span>
                        <span className="text-[10px] font-semibold text-slate-500 bg-slate-100 border border-slate-200 rounded px-1.5 py-0.5">
                          {totalLines} line{totalLines === 1 ? '' : 's'}
                        </span>
                        <span className="text-[10px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5">
                          {nz(grandTotal)} total
                        </span>
                      </>
                    )}
                    {hasErr && (
                      <span className="text-[10px] font-semibold text-amber-800 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5 inline-flex items-center gap-1">
                        <AlertTriangle size={10} /> {o.error}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <a
                      href={openFileHref(c.child_id)}
                      target="_blank"
                      rel="noreferrer"
                      className="px-3 py-1.5 rounded-md text-xs font-semibold text-teal-700 bg-white border border-teal-300 hover:bg-teal-50 inline-flex items-center gap-1.5"
                      data-testid={`wf-mat-review-openfile-${c.child_id}`}
                    >
                      <FileText size={12} /> Open file
                    </a>
                    <button
                      type="button"
                      onClick={() => openChildMaterialsTab(c.child_id)}
                      className="px-3 py-1.5 rounded-md text-xs font-semibold text-teal-700 bg-white border border-teal-300 hover:bg-teal-50 inline-flex items-center gap-1.5"
                      data-testid={`wf-mat-review-open-${c.child_id}`}
                    >
                      <ExternalLink size={12} /> Open Materials tab
                    </button>
                  </div>
                </div>

                {!hasErr && suppliers.length > 0 && (
                  <div className="mb-2 flex flex-wrap gap-1">
                    {suppliers.map(s => (
                      <span key={s.supplier} className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 border border-slate-200 text-slate-700">
                        {s.supplier}: <b>{s.materials?.length || 0}</b> · {nz(s.total || 0)}
                      </span>
                    ))}
                  </div>
                )}

                <label className="flex items-center gap-2 pt-2 border-t border-slate-100 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={reviewed}
                    onChange={() => toggleReviewed(c.child_id)}
                    className="rounded"
                    data-testid={`wf-mat-review-reviewed-${c.child_id}`}
                  />
                  <span className={`text-xs font-medium ${reviewed ? 'text-teal-800' : 'text-slate-600'}`}>
                    Reviewed &amp; ready
                  </span>
                </label>
              </div>
            );
          })}
        </div>
      )}

      <div className="flex items-center justify-between gap-3 pt-2">
        {canBack ? (
          <button onClick={onBack} className="px-3 py-1.5 rounded-md text-sm text-slate-600 hover:bg-slate-100" data-testid="wf-mat-review-back">← Back</button>
        ) : <span />}
        <div className="flex gap-2">
          {onCancel && <button onClick={onCancel} className="px-3 py-1.5 rounded-md text-sm text-slate-600 hover:bg-slate-100" data-testid="wf-mat-review-cancel">Cancel</button>}
          <button
            onClick={onNext}
            disabled={!allReviewed}
            className="px-4 py-1.5 rounded-md text-sm bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-40 inline-flex items-center gap-2"
            data-testid="wf-mat-review-continue"
          >
            <CheckCircle2 className="w-4 h-4" /> Continue
          </button>
        </div>
      </div>
    </div>
  );
}
